import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  EyeIcon,
  MegaphoneIcon,
  PhotoIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import AdminLayout from '../../components/layout/AdminLayout';
import { DEFAULT_POPUP_LINK_NOTICE_TEXT, PopupBanner } from '../../types';
import {
  POPUP_IMAGE_GUIDES,
  PopupBannerFormState,
  createEmptyPopupFormState,
  createPopupBanner,
  deletePopupAsset,
  deletePopupBanner,
  formatPopupBannerDateTime,
  getPopupBannerDisplayStatus,
  listPopupBanners,
  publishPopupBanner,
  toIsoDatetimeOrNull,
  toPopupBannerFormState,
  updatePopupBanner,
  uploadPopupAsset,
} from '../../services/popupService';
import { formatFileSize, revokePreviewUrl, validateImageFile } from '../../utils/imageCompressor';

type PendingAsset = {
  file: File;
  previewUrl: string;
};

const PopupSettingsPage: React.FC = () => {
  const [popups, setPopups] = useState<PopupBanner[]>([]);
  const [selectedPopupId, setSelectedPopupId] = useState<string | null>(null);
  const [form, setForm] = useState<PopupBannerFormState>(createEmptyPopupFormState());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [webPendingAsset, setWebPendingAsset] = useState<PendingAsset | null>(null);
  const [mobilePendingAsset, setMobilePendingAsset] = useState<PendingAsset | null>(null);
  const [removeWebAsset, setRemoveWebAsset] = useState(false);
  const [removeMobileAsset, setRemoveMobileAsset] = useState(false);

  const selectedPopup = useMemo(
    () => popups.find((popup) => popup.id === selectedPopupId) || null,
    [popups, selectedPopupId],
  );

  const summary = useMemo(() => {
    return popups.reduce(
      (acc, popup) => {
        const displayStatus = getPopupBannerDisplayStatus(popup);
        acc.total += 1;
        if (displayStatus === 'active') acc.active += 1;
        if (displayStatus === 'scheduled') acc.scheduled += 1;
        if (popup.updated_at > acc.lastUpdatedAt) acc.lastUpdatedAt = popup.updated_at;
        return acc;
      },
      { total: 0, active: 0, scheduled: 0, lastUpdatedAt: '' },
    );
  }, [popups]);

  useEffect(() => {
    void loadPopups();
    return () => {
      if (webPendingAsset) revokePreviewUrl(webPendingAsset.previewUrl);
      if (mobilePendingAsset) revokePreviewUrl(mobilePendingAsset.previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPopups = async () => {
    try {
      setLoading(true);
      setError('');
      const items = await listPopupBanners();
      setPopups(items);
      if (selectedPopupId) {
        const refreshed = items.find((item) => item.id === selectedPopupId);
        if (refreshed) {
          setForm(toPopupBannerFormState(refreshed));
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '팝업 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const clearPendingAsset = (target: 'web' | 'mobile') => {
    if (target === 'web' && webPendingAsset) {
      revokePreviewUrl(webPendingAsset.previewUrl);
      setWebPendingAsset(null);
      return;
    }
    if (target === 'mobile' && mobilePendingAsset) {
      revokePreviewUrl(mobilePendingAsset.previewUrl);
      setMobilePendingAsset(null);
    }
  };

  const resetEditor = () => {
    clearPendingAsset('web');
    clearPendingAsset('mobile');
    setRemoveWebAsset(false);
    setRemoveMobileAsset(false);
    setSelectedPopupId(null);
    setForm(createEmptyPopupFormState());
    setError('');
    setSuccessMessage('');
  };

  const handleSelectPopup = (popup: PopupBanner) => {
    clearPendingAsset('web');
    clearPendingAsset('mobile');
    setRemoveWebAsset(false);
    setRemoveMobileAsset(false);
    setSelectedPopupId(popup.id);
    setForm(toPopupBannerFormState(popup));
    setError('');
    setSuccessMessage('');
  };

  const handleFieldChange = (field: keyof PopupBannerFormState, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAssetSelect = (target: 'web' | 'mobile', file?: File) => {
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error || '이미지 파일 검증에 실패했습니다.');
      return;
    }

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('JPG, PNG, WEBP 형식만 지원합니다.');
      return;
    }

    clearPendingAsset(target);
    const previewUrl = URL.createObjectURL(file);
    const nextAsset = { file, previewUrl };

    if (target === 'web') {
      setWebPendingAsset(nextAsset);
      setRemoveWebAsset(false);
    } else {
      setMobilePendingAsset(nextAsset);
      setRemoveMobileAsset(false);
    }

    setError('');
  };

  const handleRemoveAsset = (target: 'web' | 'mobile') => {
    clearPendingAsset(target);
    if (target === 'web') {
      setRemoveWebAsset(true);
      setForm((prev) => ({
        ...prev,
        web_image_url: null,
        web_storage_path: null,
        web_width: null,
        web_height: null,
      }));
      return;
    }

    setRemoveMobileAsset(true);
    setForm((prev) => ({
      ...prev,
      mobile_image_url: null,
      mobile_storage_path: null,
      mobile_width: null,
      mobile_height: null,
    }));
  };

  const persistPopup = async (mode: 'save' | 'publish') => {
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const title = form.title.trim();
      if (!title) {
        throw new Error('팝업 제목을 입력해 주세요.');
      }

      const startAt = toIsoDatetimeOrNull(form.start_at);
      const endAt = toIsoDatetimeOrNull(form.end_at);
      if (startAt && endAt && new Date(startAt) > new Date(endAt)) {
        throw new Error('노출 시작일은 종료일보다 늦을 수 없습니다.');
      }

      const existingPopup = selectedPopup;
      const basePayload = {
        title,
        admin_title: form.admin_title.trim() || null,
        link_notice_text: form.link_notice_text.trim(),
        alt_text: form.alt_text.trim() || null,
        link_url: form.link_url.trim() || null,
        link_target: form.link_target,
        priority: Number(form.priority) || 1,
        dismiss_days: Number(form.dismiss_days) || 7,
        start_at: startAt,
        end_at: endAt,
        status: existingPopup?.status || 'draft',
        is_active: existingPopup?.is_active ?? false,
      };

      let savedPopup = existingPopup
        ? await updatePopupBanner(existingPopup.id, basePayload)
        : await createPopupBanner({ placement: 'service-entry', ...basePayload });

      const assetPatch: Record<string, unknown> = {};

      if (removeWebAsset && existingPopup?.web_storage_path && !webPendingAsset) {
        await deletePopupAsset(existingPopup.web_storage_path);
        assetPatch.web_image_url = null;
        assetPatch.web_storage_path = null;
        assetPatch.web_width = null;
        assetPatch.web_height = null;
      }

      if (removeMobileAsset && existingPopup?.mobile_storage_path && !mobilePendingAsset) {
        await deletePopupAsset(existingPopup.mobile_storage_path);
        assetPatch.mobile_image_url = null;
        assetPatch.mobile_storage_path = null;
        assetPatch.mobile_width = null;
        assetPatch.mobile_height = null;
      }

      if (webPendingAsset) {
        const uploadedWebAsset = await uploadPopupAsset({
          popupId: savedPopup.id,
          target: 'web',
          file: webPendingAsset.file,
          previousStoragePath: existingPopup?.web_storage_path,
        });
        assetPatch.web_image_url = uploadedWebAsset.imageUrl;
        assetPatch.web_storage_path = uploadedWebAsset.storagePath;
        assetPatch.web_width = uploadedWebAsset.width;
        assetPatch.web_height = uploadedWebAsset.height;
      }

      if (mobilePendingAsset) {
        const uploadedMobileAsset = await uploadPopupAsset({
          popupId: savedPopup.id,
          target: 'mobile',
          file: mobilePendingAsset.file,
          previousStoragePath: existingPopup?.mobile_storage_path,
        });
        assetPatch.mobile_image_url = uploadedMobileAsset.imageUrl;
        assetPatch.mobile_storage_path = uploadedMobileAsset.storagePath;
        assetPatch.mobile_width = uploadedMobileAsset.width;
        assetPatch.mobile_height = uploadedMobileAsset.height;
      }

      if (Object.keys(assetPatch).length > 0) {
        savedPopup = await updatePopupBanner(savedPopup.id, assetPatch);
      }

      const webReady = !!(assetPatch.web_image_url ?? savedPopup.web_image_url);
      const mobileReady = !!(assetPatch.mobile_image_url ?? savedPopup.mobile_image_url);

      if (mode === 'publish') {
        if (!webReady || !mobileReady) {
          throw new Error('게시하려면 웹용/모바일용 이미지를 모두 등록해야 합니다.');
        }
        savedPopup = await publishPopupBanner(savedPopup.id, 'publish');
      }

      await loadPopups();
      handleSelectPopup(savedPopup);
      clearPendingAsset('web');
      clearPendingAsset('mobile');
      setRemoveWebAsset(false);
      setRemoveMobileAsset(false);
      setSuccessMessage(mode === 'publish' ? '팝업을 게시했습니다.' : '팝업을 저장했습니다.');
    } catch (err) {
      setError(err instanceof Error ? err.message : '팝업 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      setPublishing(true);
      await persistPopup('publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleDeactivate = async () => {
    if (!selectedPopup) return;

    try {
      setPublishing(true);
      setError('');
      setSuccessMessage('');
      const deactivated = await publishPopupBanner(selectedPopup.id, 'deactivate');
      await loadPopups();
      handleSelectPopup(deactivated);
      setSuccessMessage('팝업 노출을 중지했습니다.');
    } catch (err) {
      setError(err instanceof Error ? err.message : '팝업 비활성화에 실패했습니다.');
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPopup) return;
    const confirmed = window.confirm('선택한 팝업을 삭제하시겠습니까? 삭제한 이미지는 복구되지 않습니다.');
    if (!confirmed) return;

    try {
      setSaving(true);
      await deletePopupBanner(selectedPopup);
      resetEditor();
      await loadPopups();
      setSuccessMessage('팝업을 삭제했습니다.');
    } catch (err) {
      setError(err instanceof Error ? err.message : '팝업 삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const getBadgeClass = (popup: PopupBanner) => {
    switch (getPopupBannerDisplayStatus(popup)) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'scheduled':
        return 'bg-blue-100 text-blue-700';
      case 'ended':
        return 'bg-orange-100 text-orange-700';
      case 'archived':
        return 'bg-gray-200 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getPreviewImage = (target: 'web' | 'mobile') => {
    if (target === 'web') {
      return webPendingAsset?.previewUrl || form.web_image_url || '';
    }
    return mobilePendingAsset?.previewUrl || form.mobile_image_url || '';
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">팝업설정</h1>
            <p className="mt-1 text-sm text-gray-600">웹/모바일 서비스 진입 팝업을 관리하고 미리보기/게시 상태를 제어합니다.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={resetEditor}
              className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <PlusIcon className="mr-2 h-5 w-5" />
              신규 팝업 등록
            </button>
            <button
              type="button"
              onClick={() => void loadPopups()}
              className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowPathIcon className="mr-2 h-5 w-5" />
              새로고침
            </button>
          </div>
        </div>

        {error && <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
        {successMessage && (
          <div className="rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-700">{successMessage}</div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="rounded-xl bg-white p-5 shadow">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-[#1B365D] p-3 text-white">
                <MegaphoneIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">전체 팝업</p>
                <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl bg-white p-5 shadow">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-green-500 p-3 text-white">
                <CheckCircleIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">노출 중</p>
                <p className="text-2xl font-bold text-gray-900">{summary.active}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl bg-white p-5 shadow">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-500 p-3 text-white">
                <EyeIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">예약됨</p>
                <p className="text-2xl font-bold text-gray-900">{summary.scheduled}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl bg-white p-5 shadow">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-amber-500 p-3 text-white">
                <PhotoIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">최근 수정</p>
                <p className="text-sm font-semibold text-gray-900">{summary.lastUpdatedAt ? formatPopupBannerDateTime(summary.lastUpdatedAt) : '-'}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
          <div className="rounded-2xl bg-white shadow">
            <div className="border-b border-gray-100 px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-900">팝업 목록</h2>
              <p className="mt-1 text-sm text-gray-500">저장된 팝업을 선택해 수정하거나 게시 상태를 확인하세요.</p>
            </div>
            {loading ? (
              <div className="p-6 text-sm text-gray-500">목록을 불러오는 중...</div>
            ) : popups.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">등록된 팝업이 없습니다. 신규 팝업을 만들어 주세요.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {popups.map((popup) => (
                  <button
                    key={popup.id}
                    type="button"
                    onClick={() => handleSelectPopup(popup)}
                    className={`w-full px-5 py-4 text-left transition hover:bg-gray-50 ${
                      popup.id === selectedPopupId ? 'bg-blue-50/60' : 'bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{popup.title}</p>
                        <p className="mt-1 text-xs text-gray-500">수정일 {formatPopupBannerDateTime(popup.updated_at)}</p>
                      </div>
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-medium ${getBadgeClass(popup)}`}>
                        {getPopupBannerDisplayStatus(popup) === 'active' && '노출중'}
                        {getPopupBannerDisplayStatus(popup) === 'scheduled' && '예약됨'}
                        {getPopupBannerDisplayStatus(popup) === 'ended' && '종료됨'}
                        {getPopupBannerDisplayStatus(popup) === 'archived' && '보관'}
                        {getPopupBannerDisplayStatus(popup) === 'inactive' && '비활성'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-6 rounded-2xl bg-white p-6 shadow">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{selectedPopup ? '팝업 수정' : '신규 팝업 등록'}</h2>
                <p className="mt-1 text-sm text-gray-500">웹/모바일 업로드를 모두 등록해야 게시할 수 있습니다.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void persistPopup('save')}
                  disabled={saving || publishing}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  저장
                </button>
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={saving || publishing}
                  className="rounded-md bg-[#1B365D] px-4 py-2 text-sm font-medium text-white hover:bg-[#162c4b] disabled:opacity-50"
                >
                  게시
                </button>
                {selectedPopup && (
                  <>
                    <button
                      type="button"
                      onClick={handleDeactivate}
                      disabled={saving || publishing}
                      className="rounded-md bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                    >
                      노출 중지
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={saving || publishing}
                      className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      삭제
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">팝업 제목</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(event) => handleFieldChange('title', event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#1B365D] focus:outline-none focus:ring-1 focus:ring-[#1B365D]"
                  placeholder="예: 봄맞이 신규 혜택 안내"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">관리용 제목</label>
                <input
                  type="text"
                  value={form.admin_title}
                  onChange={(event) => handleFieldChange('admin_title', event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#1B365D] focus:outline-none focus:ring-1 focus:ring-[#1B365D]"
                  placeholder="예: 2026-봄 프로모션 팝업"
                />
              </div>
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-gray-700">팝업 안내 문구</label>
                <input
                  type="text"
                  value={form.link_notice_text}
                  onChange={(event) => handleFieldChange('link_notice_text', event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#1B365D] focus:outline-none focus:ring-1 focus:ring-[#1B365D]"
                  placeholder={DEFAULT_POPUP_LINK_NOTICE_TEXT}
                />
                <p className="mt-2 text-xs text-gray-500">팝업 상단에 노출되는 안내 문구입니다. 비워두면 안내 문구가 표시되지 않습니다.</p>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">링크 URL</label>
                <input
                  type="url"
                  value={form.link_url}
                  onChange={(event) => handleFieldChange('link_url', event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#1B365D] focus:outline-none focus:ring-1 focus:ring-[#1B365D]"
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">링크 열기 방식</label>
                <select
                  value={form.link_target}
                  onChange={(event) => handleFieldChange('link_target', event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#1B365D] focus:outline-none focus:ring-1 focus:ring-[#1B365D]"
                >
                  <option value="_blank">새 탭</option>
                  <option value="_self">현재 탭</option>
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">노출 시작</label>
                <input
                  type="datetime-local"
                  value={form.start_at}
                  onChange={(event) => handleFieldChange('start_at', event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#1B365D] focus:outline-none focus:ring-1 focus:ring-[#1B365D]"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">노출 종료</label>
                <input
                  type="datetime-local"
                  value={form.end_at}
                  onChange={(event) => handleFieldChange('end_at', event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#1B365D] focus:outline-none focus:ring-1 focus:ring-[#1B365D]"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">우선순위</label>
                <input
                  type="number"
                  min={1}
                  value={form.priority}
                  onChange={(event) => handleFieldChange('priority', Number(event.target.value))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#1B365D] focus:outline-none focus:ring-1 focus:ring-[#1B365D]"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">숨김 유지 일수</label>
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={form.dismiss_days}
                  onChange={(event) => handleFieldChange('dismiss_days', Number(event.target.value))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#1B365D] focus:outline-none focus:ring-1 focus:ring-[#1B365D]"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">대체 텍스트</label>
              <input
                type="text"
                value={form.alt_text}
                onChange={(event) => handleFieldChange('alt_text', event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#1B365D] focus:outline-none focus:ring-1 focus:ring-[#1B365D]"
                placeholder="팝업 이미지 설명(접근성용)"
              />
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              {(['web', 'mobile'] as const).map((target) => {
                const guide = POPUP_IMAGE_GUIDES[target];
                const previewImage = getPreviewImage(target);
                const pendingAsset = target === 'web' ? webPendingAsset : mobilePendingAsset;
                return (
                  <div key={target} className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">{guide.label}</h3>
                        <p className="mt-1 text-sm text-gray-600">{guide.helperText}</p>
                      </div>
                      <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">권장 {guide.recommendedSize}</span>
                    </div>
                    <div className="mt-4 space-y-1 text-xs text-gray-500">
                      <p>최소 기준: {guide.minimumSize}</p>
                      <p>비율: {guide.ratio}</p>
                      <p>권장 용량: {guide.maxFileSizeLabel}</p>
                    </div>

                    <div className="mt-4 overflow-hidden rounded-2xl border border-dashed border-gray-300 bg-white">
                      {previewImage ? (
                        <img src={previewImage} alt={`${guide.label} 미리보기`} className="mx-auto h-auto max-h-[420px] w-full object-contain" />
                      ) : (
                        <div className="flex h-[320px] flex-col items-center justify-center text-center text-sm text-gray-400">
                          <PhotoIcon className="mb-3 h-10 w-10" />
                          {guide.label}이 아직 등록되지 않았습니다.
                        </div>
                      )}
                    </div>

                    {pendingAsset && (
                      <p className="mt-3 text-xs text-gray-500">
                        선택 파일: {pendingAsset.file.name} · {formatFileSize(pendingAsset.file.size)}
                      </p>
                    )}

                    <div className="mt-4 flex flex-wrap gap-2">
                      <label className="inline-flex cursor-pointer items-center rounded-md bg-[#1B365D] px-4 py-2 text-sm font-medium text-white hover:bg-[#162c4b]">
                        이미지 선택
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="sr-only"
                          onChange={(event) => handleAssetSelect(target, event.target.files?.[0])}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => handleRemoveAsset(target)}
                        className="inline-flex items-center rounded-md border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                      >
                        <TrashIcon className="mr-2 h-4 w-4" />
                        이미지 제거
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              {(['web', 'mobile'] as const).map((target) => {
                const previewImage = getPreviewImage(target);
                return (
                  <div key={`${target}-preview`} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-semibold text-gray-900">{target === 'web' ? '웹 미리보기' : '모바일 미리보기'}</h3>
                      <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">{target === 'web' ? 'Desktop' : 'Mobile'}</span>
                    </div>
                    <div className="mt-4 rounded-3xl border border-gray-200 bg-gray-50 p-4">
                      <div className={`mx-auto overflow-hidden rounded-2xl bg-white shadow ${target === 'web' ? 'max-w-[320px]' : 'max-w-[240px]'}`}>
                        <div className="border-b border-gray-100 px-4 py-3">
                          <p className="text-sm font-semibold text-gray-900">{form.title || '팝업 제목 미리보기'}</p>
                          {form.link_notice_text.trim() && (
                            <p className="mt-1 text-[11px] text-gray-500">{form.link_notice_text.trim()}</p>
                          )}
                        </div>
                        {previewImage ? (
                          <img src={previewImage} alt={`${target} 팝업 미리보기`} className="w-full object-contain" />
                        ) : (
                          <div className="flex h-[280px] items-center justify-center text-sm text-gray-400">등록된 이미지가 없습니다.</div>
                        )}
                        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-xs text-gray-600">
                          <span>일주일 동안 보지 않기</span>
                          <span className="rounded-lg bg-[#1B365D] px-3 py-1 font-semibold text-white">닫기</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PopupSettingsPage;
